import type { Plane } from './Plane'
import { Vector3 } from './Vector3'
import type { Vector3Like } from './types'

/** A half line: origin + t * direction for t >= 0 (`direction` is unit length). */
export class Ray {
  /** As three's Ray, the vectors passed in are kept (not copied). */
  constructor(public origin: Vector3 = new Vector3(), public direction: Vector3 = new Vector3(0, 0, -1)) {}

  set(origin: Vector3Like, direction: Vector3Like): this {
    this.origin.copy(origin)
    this.direction.copy(direction)
    return this
  }

  copy(ray: Ray): this {
    return this.set(ray.origin, ray.direction)
  }

  clone(): Ray {
    return new Ray(this.origin.clone(), this.direction.clone())
  }

  at(t: number, target: Vector3): Vector3 {
    return target.copy(this.origin).addScaledVector(this.direction, t)
  }

  /** Distance along the ray to `plane`, or null when it is behind or parallel (0 when the origin lies on it). */
  distanceToPlane(plane: Plane): number | null {
    const denominator = plane.normal.dot(this.direction)
    if (denominator === 0) {
      if (plane.distanceToPoint(this.origin) === 0) return 0
      return null
    }
    const t = -(this.origin.dot(plane.normal) + plane.constant) / denominator
    return t >= 0 ? t : null
  }

  intersectPlane(plane: Plane, target: Vector3): Vector3 | null {
    const t = this.distanceToPlane(plane)
    if (t === null) return null
    return this.at(t, target)
  }
}
